'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { NavigationMenuItem } from '@kit/ui/navigation-menu';
import { cn, isRouteActive } from '@kit/ui/utils';

import {
  isInternalHashLink,
  isPlainClick,
  resolveSamePageHash,
  scrollToHashTarget,
} from './_lib/hash-scroll';

const getClassName = (path: string, currentPathName: string) => {
  const isActive = isRouteActive(path, currentPathName);

  return cn(
    `inline-flex w-max text-sm font-medium transition-colors duration-200 px-2 py-1`,

    {
      'text-secondary/70 hover:text-secondary': !isActive,
      'text-secondary': isActive,
    },
  );
};

export function SiteNavigationItem({
  path,
  children,
}: React.PropsWithChildren<{
  path: string;
}>) {
  const currentPathName = usePathname();
  const className = getClassName(path, currentPathName);

  // Every item renders as a Next <Link>, so it keeps prefetch + client-side
  // routing on ALL routes (a native <a> forced a full document reload whenever
  // a "/#contact" header link was clicked from a non-landing route like /book).
  // For an app-internal fragment link we additionally intercept a genuine
  // same-page click to SMOOTH-SCROLL: resolveSamePageHash returns the id only
  // when the href targets the current document, and scrollToHashTarget +
  // preventDefault suppress Link's own navigation (Next's Link bails when the
  // event is already defaultPrevented). A cross-route hash click, a missing
  // target, or a Cmd/Ctrl/middle click all skip preventDefault, so Link
  // soft-navigates. External (https://…/#x) links never match isInternalHashLink.
  const onClick = isInternalHashLink(path)
    ? (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (!isPlainClick(event)) return;
        const id = resolveSamePageHash(path);
        if (id && scrollToHashTarget(id)) {
          event.preventDefault();
        }
      }
    : undefined;

  return (
    <NavigationMenuItem key={path}>
      <Link
        className={className}
        href={path}
        as={path}
        onClick={onClick}
      >
        {children}
      </Link>
    </NavigationMenuItem>
  );
}
